import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, isTouchDevice } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'always'
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * The floating stack wrapper (the header plus any bar glued above it).
   * @returns {Element}
   */
  get #stack() {
    return this.closest('#header-floating-stack') ?? this;
  }

  /**
   * Observes a zero-height sentinel placed right above the floating stack to track when it's
   * actively sticky, instead of offsetting the stack itself by -1px to observe it directly -
   * that offset was visibly shifting the header by a pixel on every scroll.
   */
  #observeStickyPosition = () => {
    if (this.#intersectionObserver) return;

    const sentinel = document.getElementById('header-sticky-sentinel') ?? this.#stack;

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
    });

    this.#intersectionObserver.observe(sentinel);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
      setHeaderMenuStyle();
      return;
    }

    this.#menuDrawerHiddenWidth = null;

    // The overflow-list's own ResizeObserver can't fire while its container is
    // display:none (drawer mode), so its minimum-reached state may be stale from
    // before it was hidden. Show the menu directly so it can re-measure with real
    // dimensions; if items still don't fit, its own overflowMinimum event calls
    // #handleOverflowMinimum again and flips us back to drawer.
    if (this.dataset.menuStyle === 'drawer' && !isTouchDevice()) {
      this.dataset.menuStyle = 'menu';
      return;
    }

    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.#stack.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (isAtTop) {
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      this.dataset.scrollDirection = 'up';
    } else {
      this.dataset.scrollDirection = 'down';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    if (this.getAttribute('sticky') === 'always') {
      this.#observeStickyPosition();
      document.addEventListener('scroll', this.#handleWindowScroll);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const headerGroup = document.querySelector('#header-group');
  const headerStack = document.querySelector('#header-floating-stack');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Keep the page-content offset in sync with the floating stack's real height
  // (the bar glued above the header, if any, plus the header itself)
  if (headerStack instanceof HTMLElement) {
    const stackResizeObserver = new ResizeObserver((entries) => {
      const blockSize = entries[0]?.borderBoxSize[0]?.blockSize ?? headerStack.offsetHeight;
      document.body.style.setProperty('--header-stack-height', `${Math.round(blockSize)}px`);
    });
    stackResizeObserver.observe(headerStack);
  }

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce(
        (totalHeight, entry) => totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0),
        0
      );
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
